import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme, useLanguage, API } from '@/App';
import { getTranslation } from '@/translations';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import Sidebar from '@/components/Sidebar';
import AddressSearch from '@/components/AddressSearch';
import PayAsYouGoCard from '@/components/PayAsYouGoCard';
import {
  User,
  Mail,
  Lock,
  Save,
  Eye,
  EyeOff,
  Menu,
  Truck,
  MapPin,
  Package,
  CreditCard,
  Wallet,
  Building2,
  Plus,
  Trash2,
  Edit2,
  Bell,
  Send,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  Upload,
  Clock,
  Zap,
  Store,
  Crown,
  Calendar,
  ArrowUpRight,
  History,
  Receipt,
  Home,
  MessageSquarePlus,
  Info,
  BarChart3,
  Lightbulb,
  DollarSign,
  Pin,
  HelpCircle,
  FileText as FileTextIcon,
  Rocket,
  X,
  ArrowDownRight,
  AlertTriangle,
  QrCode
} from 'lucide-react';

import { usePushNotifications } from '@/utils/pwa';

// Senin=0 ... Minggu=6 (mengikuti weekday() Python di backend)
const DAY_LABELS = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'];


// ============================================================
// Push Notification Settings Card
// ============================================================
const PushNotificationCard = () => {
  const { user, token } = useAuth();
  const { permission, isSupported, isSubscribed, requestAndSubscribe, unsubscribe } = usePushNotifications();
  const [loading, setLoading] = useState(false);

  if (!isSupported) {
    return (
      <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-100 dark:bg-zinc-800">
            <Bell className="w-5 h-5 text-slate-500 dark:text-zinc-400" />
          </div>
          <div>
            <p className="font-medium text-slate-900 dark:text-zinc-50">Push Notification</p>
            <p className="text-xs text-slate-500 dark:text-zinc-400">Browser tidak mendukung push notification</p>
          </div>
        </div>
      </div>
    );
  }

  const handleEnable = async () => {
    setLoading(true);
    // Re-check permission first (might have changed in OS/browser settings)
    const currentPerm = 'Notification' in window ? Notification.permission : 'denied';
    if (currentPerm === 'denied') {
      toast.error('Notifikasi masih diblokir. Pastikan sudah diizinkan di pengaturan browser, lalu refresh halaman.');
      setLoading(false);
      return;
    }
    const result = await requestAndSubscribe(user?.id, token);
    if (result) {
      toast.success('Push notification berhasil diaktifkan');
    } else {
      const perm = 'Notification' in window ? Notification.permission : 'denied';
      if (perm === 'denied') {
        toast.error('Notifikasi diblokir. Buka pengaturan browser untuk mengizinkan.');
      } else {
        toast.error('Gagal mengaktifkan push notification. Coba refresh halaman.');
      }
    }
    setLoading(false);
  };

  const handleDisable = async () => {
    setLoading(true);
    await unsubscribe(user?.id, token);
    toast.success('Push notification dinonaktifkan');
    setLoading(false);
  };

  return (
    <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800" data-testid="push-notification-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${permission === 'granted' && isSubscribed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
            <Bell className={`w-5 h-5 ${permission === 'granted' && isSubscribed ? 'text-green-600' : 'text-amber-600'} dark:text-green-400`} />
          </div>
          <div>
            <p className="font-medium text-slate-900 dark:text-zinc-50">Push Notification</p>
            <p className="text-xs text-slate-500 dark:text-zinc-400">Notifikasi langsung di HP/browser saat ada pesan baru</p>
          </div>
        </div>
        <Switch
          checked={permission === 'granted' && isSubscribed}
          onCheckedChange={(checked) => checked ? handleEnable() : handleDisable()}
          disabled={loading}
          data-testid="push-notification-toggle"
        />
      </div>
      {permission === 'granted' && isSubscribed ? (
        <div className="flex items-center gap-2 text-green-600 text-sm dark:text-green-400">
          <CheckCircle className="w-4 h-4" />
          <span>Push notification aktif - Anda akan menerima notifikasi real-time</span>
        </div>
      ) : permission === 'denied' ? (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-sm">
          <p className="text-red-700 dark:text-red-400 font-medium">Notifikasi Diblokir</p>
          <p className="text-red-600/70 text-xs mt-1 dark:text-red-400">Buka pengaturan browser &gt; Izin Situs &gt; Notifikasi untuk mengizinkan, lalu refresh halaman ini.</p>
        </div>
      ) : (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-sm">
          <p className="text-amber-700 dark:text-amber-400">Aktifkan untuk menerima notifikasi pesan baru di HP/browser walaupun aplikasi tidak terbuka.</p>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Follow Up Settings Component
// ============================================================
const FollowUpSettings = () => {
  const { user, token } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    ai_personalization: true,
    unpaid_order: {
      enabled: false,
      delay_hours: 2,
      max_per_conversation: 1,
      template: 'Halo kak {nama}, pesanan {produk} senilai {total} belum dibayar nih. Ada kendala? Kami siap bantu'
    },
    interest_reminder: {
      enabled: false,
      delay_hours: 3,
      max_per_conversation: 1,
      template: 'Halo kak {nama}, masih tertarik dengan {produk}? Stok terbatas lho, jangan sampai kehabisan ya!'
    }
  });
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total_sent_this_month: 0, total_converted: 0, conversion_rate: 0 });

  useEffect(() => {
    fetchSettings();
    fetchLogs();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/follow-up/settings`, { headers });
      if (res.data.success && res.data.settings) {
        setSettings(res.data.settings);
      }
    } catch (err) {
      console.error('Error fetching follow-up settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/api/follow-up/logs?limit=5`, { headers });
      if (res.data.success) {
        setLogs(res.data.logs || []);
        setStats(res.data.stats || {});
      }
    } catch (err) {
      console.error('Error fetching follow-up logs:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/follow-up/settings`, settings, { headers });
      toast.success('Pengaturan follow-up berhasil disimpan');
    } catch (err) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const updateType = (type, field, value) => {
    setSettings(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const delayOptions = [
    { value: 1, label: '1 jam' },
    { value: 2, label: '2 jam' },
    { value: 3, label: '3 jam' },
    { value: 4, label: '4 jam' },
    { value: 6, label: '6 jam' },
    { value: 12, label: '12 jam' }
  ];

  const FollowUpTypeCard = ({ title, description, icon: Icon, iconColor, type, typeSettings }) => (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${iconColor}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-zinc-50">{title}</h3>
              <p className="text-xs text-slate-500 dark:text-zinc-400">{description}</p>
            </div>
          </div>
          <Switch
            checked={typeSettings.enabled}
            onCheckedChange={(v) => updateType(type, 'enabled', v)}
            data-testid={`followup-${type}-toggle`}
          />
        </div>

        {typeSettings.enabled && (
          <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-zinc-800">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs sm:text-sm mb-1.5 block">Kirim setelah</Label>
                <Select
                  value={String(typeSettings.delay_hours)}
                  onValueChange={(v) => updateType(type, 'delay_hours', parseInt(v))}
                >
                  <SelectTrigger className="text-sm" data-testid={`followup-${type}-delay`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {delayOptions.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1 dark:text-zinc-400">Setelah chat terakhir customer (maks 24 jam)</p>
              </div>
              <div>
                <Label className="text-xs sm:text-sm mb-1.5 block">Maks follow-up</Label>
                <Select
                  value={String(typeSettings.max_per_conversation)}
                  onValueChange={(v) => updateType(type, 'max_per_conversation', parseInt(v))}
                >
                  <SelectTrigger className="text-sm" data-testid={`followup-${type}-max`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1x per percakapan</SelectItem>
                    <SelectItem value="2">2x per percakapan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs sm:text-sm mb-1.5 block">Template pesan</Label>
              <Textarea
                value={typeSettings.template}
                onChange={(e) => updateType(type, 'template', e.target.value)}
                rows={3}
                className="text-sm"
                placeholder="Tulis template pesan..."
                data-testid={`followup-${type}-template`}
              />
              <p className="text-[10px] text-slate-500 mt-1 dark:text-zinc-400">Gunakan {'{nama}'}, {'{produk}'}, {'{total}'} untuk personalisasi</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Global Toggle */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
              <MessageSquarePlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-zinc-50">Auto Follow-Up</h2>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Kirim pesan follow-up otomatis ke customer</p>
            </div>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => setSettings(prev => ({ ...prev, enabled: v }))}
            data-testid="followup-global-toggle"
          />
        </div>

        {/* 24-hour info */}
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-blue-100 dark:border-indigo-800">
          <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
          <p className="text-xs text-indigo-700 dark:text-indigo-300">
            Follow-up hanya dikirim dalam <strong>24 jam</strong> setelah pesan terakhir customer (Meta 24-Hour Window). Tidak ada biaya tambahan karena masih dalam window customer-initiated.
          </p>
        </div>
      </div>

      {settings.enabled && (
        <>
          {/* Belum Bayar */}
          <FollowUpTypeCard
            title="Belum Bayar"
            description="Ingatkan customer yang sudah order tapi belum bayar"
            icon={CreditCard}
            iconColor="bg-gradient-to-br from-amber-500 to-orange-600"
            type="unpaid_order"
            typeSettings={settings.unpaid_order}
          />

          {/* Minat Belum Order */}
          <FollowUpTypeCard
            title="Minat Tapi Belum Order"
            description="Follow-up customer yang tanya produk tapi belum buat order"
            icon={MessageCircle}
            iconColor="bg-gradient-to-br from-emerald-500 to-teal-600"
            type="interest_reminder"
            typeSettings={settings.interest_reminder}
          />

          {/* AI Personalization */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-zinc-50">AI Personalisasi</h3>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">AI akan menyesuaikan pesan berdasarkan konteks percakapan</p>
                </div>
              </div>
              <Switch
                checked={settings.ai_personalization}
                onCheckedChange={(v) => setSettings(prev => ({ ...prev, ai_personalization: v }))}
                data-testid="followup-ai-toggle"
              />
            </div>
          </div>

          {/* Channel Info */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-3">
              <Send className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-50">Channel Pengiriman</h3>
            </div>
            <p className="text-xs text-slate-500 dark:text-zinc-400">
              Pesan follow-up otomatis dikirim melalui channel yang sama dengan percakapan terakhir customer (WhatsApp, Instagram, atau Messenger).
            </p>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
            data-testid="followup-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Simpan Pengaturan
          </Button>

          {/* Stats & Logs */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4 sm:p-6">
            <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-blue-500" />
              Riwayat Follow-Up
            </h3>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-zinc-800/50">
                <p className="text-lg sm:text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50">{stats.total_sent_this_month}</p>
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400">Terkirim bulan ini</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-zinc-800/50">
                <p className="text-lg sm:text-xl font-bold text-emerald-600 dark:text-emerald-400">{stats.total_converted}</p>
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400">Berhasil konversi</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-zinc-800/50">
                <p className="text-lg sm:text-xl font-bold text-indigo-600 dark:text-indigo-400">{stats.conversion_rate}%</p>
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400">Conversion rate</p>
              </div>
            </div>

            {/* Logs */}
            {logs.length > 0 ? (
              <div className="divide-y divide-slate-100 dark:divide-zinc-800">
                {logs.map((log, idx) => (
                  <div key={idx} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 truncate">{log.customer_name || 'Customer'}</p>
                      <p className="text-xs text-slate-500 truncate dark:text-zinc-400">
                        {log.type === 'unpaid_order' ? 'Belum Bayar' : 'Minat'} • {log.channel || 'WhatsApp'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${log.status === 'sent' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                        {log.status === 'sent' ? 'Terkirim' : 'Gagal'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 text-xs dark:text-zinc-400">
                <Clock className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Belum ada riwayat follow-up
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Small Trial Countdown Component (inline)
const TrialCountdownSmall = ({ expiresAt }) => {
  const [timeLeft, setTimeLeft] = useState(() => {
    if (!expiresAt) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const difference = expiry - now;
    if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000)
    };
  });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  const padZero = (num) => String(num).padStart(2, '0');

  if (isExpired) {
    return <span className="font-medium text-red-600 dark:text-red-400">Berakhir</span>;
  }

  return (
    <span className="font-mono font-medium">
      {timeLeft.days > 0 && `${timeLeft.days}h `}
      {padZero(timeLeft.hours)}:{padZero(timeLeft.minutes)}:{padZero(timeLeft.seconds)}
    </span>
  );
};

// ============================================================
// Subscription Tab Component
// ============================================================
const SubscriptionTab = ({ user, token, refreshUser }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const headers = { Authorization: `Bearer ${token}` };
  const navigate = useNavigate();

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [pendingDowngrade, setPendingDowngrade] = useState(null);
  const [downgradeModal, setDowngradeModal] = useState(null); // { plan, agents, maxKeep, excessCount }
  const [selectedKeepAgents, setSelectedKeepAgents] = useState([]);
  const [downgradeProcessing, setDowngradeProcessing] = useState(false);

  const userPlan = user?.plan?.toLowerCase() || 'trial';
  const billingPeriod = user?.billing_period || 'monthly';
  const planOrder = { trial: 0, starter: 1, growth: 2, business: 3, enterprise: 4 };
  const userRank = planOrder[userPlan] || 0;

  useEffect(() => {
    if (!user?.id) return;
    // Payment history = plan subscriptions + Pay As You Go credits, paid only
    Promise.all([
      axios.get(`${API_URL}/api/payment/duitku/subscription/history/${user.id}`, { headers })
        .then(res => (res.data.payments || []).map(p => ({ ...p, kind: 'plan' })))
        .catch(() => []),
      axios.get(`${API_URL}/api/payment/duitku/topup/history`, { headers })
        .then(res => (res.data.topups || []).map(t => ({ ...t, kind: 'topup' })))
        .catch(() => []),
    ])
      .then(([plans, topups]) => {
        const merged = [...plans, ...topups]
          .filter(p => (p.status || '').toLowerCase() === 'paid')
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setPaymentHistory(merged);
      })
      .finally(() => setHistoryLoading(false));
    // Check pending downgrade
    axios.get(`${API_URL}/api/payment/duitku/subscription/pending-downgrade?user_id=${user.id}`, { headers })
      .then(res => {
        if (res.data.has_pending) setPendingDowngrade(res.data);
      })
      .catch(() => {});
  }, [user?.id]);

  const handlePlanAction = async (targetPlan) => {
    const targetRank = planOrder[targetPlan] || 0;
    if (targetRank > userRank) {
      // Upgrade → go to payment
      window.location.href = `/payment?plan=${targetPlan}`;
    } else if (targetRank < userRank) {
      // Downgrade → check eligibility first
      try {
        const res = await axios.post(`${API_URL}/api/payment/duitku/subscription/check-downgrade`, {
          user_id: user.id, new_plan: targetPlan
        }, { headers });
        const data = res.data;
        setDowngradeModal({
          plan: targetPlan,
          agents: data.agents || [],
          maxKeep: data.new_max_agents,
          excessCount: data.excess_count,
          requiresSelection: data.requires_selection,
        });
        if (!data.requires_selection) {
          setSelectedKeepAgents((data.agents || []).map(a => a.id));
        } else {
          setSelectedKeepAgents([]);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Gagal memeriksa downgrade');
      }
    }
  };

  const handleConfirmDowngrade = async () => {
    if (!downgradeModal) return;
    if (downgradeModal.requiresSelection && selectedKeepAgents.length > downgradeModal.maxKeep) {
      toast.error(`Pilih maksimal ${downgradeModal.maxKeep} AI Agent untuk dipertahankan`);
      return;
    }
    setDowngradeProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/payment/duitku/subscription/apply-downgrade`, {
        user_id: user.id,
        new_plan: downgradeModal.plan,
        keep_agent_ids: selectedKeepAgents,
      }, { headers });
      const data = res.data;
      toast.success(data.message);
      setDowngradeModal(null);
      if (data.scheduled) {
        setPendingDowngrade({ has_pending: true, new_plan: data.new_plan, effective_at: data.effective_at });
      }
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal memproses downgrade');
    } finally {
      setDowngradeProcessing(false);
    }
  };

  const handleCancelDowngrade = async () => {
    try {
      await axios.post(`${API_URL}/api/payment/duitku/subscription/cancel-downgrade?user_id=${user.id}`, {}, { headers });
      toast.success('Jadwal downgrade dibatalkan');
      setPendingDowngrade(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membatalkan downgrade');
    }
  };

  const toggleKeepAgent = (agentId) => {
    setSelectedKeepAgents(prev => {
      if (prev.includes(agentId)) return prev.filter(id => id !== agentId);
      if (prev.length >= (downgradeModal?.maxKeep || 1)) {
        toast.error(`Maksimal ${downgradeModal?.maxKeep} agent`);
        return prev;
      }
      return [...prev, agentId];
    });
  };

  const planConfig = {
    trial: { name: 'Trial', badge: 'Free 30 Days', price: 'Rp 0', color: 'blue', features: ['1 AI Agent', '500 pesan/bulan · 60/hari', 'Semua channel (WA, IG, FB)', 'CRM & Pipeline', 'Analytics'] },
    starter: { name: 'Starter', badge: 'Mulai Bisnis', price: 'Rp 199.000', color: 'blue', features: ['1 AI Agent', '1.700 pesan/bulan · 150/hari', 'Semua channel (WA, IG, FB)', 'CRM & Pipeline', 'Analytics & Reports'] },
    growth: { name: 'Growth', badge: 'RECOMMENDED', price: 'Rp 449.000', color: 'purple', features: ['3 AI Agent', '4.600 pesan/bulan · 400/hari', 'Semua channel (WA, IG, FB)', 'CRM & Pipeline', 'Priority Support'] },
    business: { name: 'Business', badge: 'Best Value', price: 'Rp 999.000', color: 'amber', features: ['10 AI Agent', '10.000 pesan/bulan · 1.000/hari', 'Semua channel (WA, IG, FB)', 'Priority Support', 'API Access'] },
    enterprise: { name: 'Enterprise', badge: 'Skala Besar', price: 'Custom', color: 'slate', features: ['Unlimited AI Agent', 'Unlimited pesan', 'Dedicated Support', 'Custom Integration', 'SLA 99.9%'] },
  };

  const current = planConfig[userPlan] || planConfig.trial;
  const colorClasses = {
    blue: 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-blue-900 dark:text-indigo-200',
    purple: 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800 text-purple-900 dark:text-purple-200',
    amber: 'from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200',
    slate: 'from-slate-50 to-gray-50 dark:from-slate-800/50 dark:to-gray-800/50 border-slate-200 dark:border-zinc-800 text-slate-900 dark:text-zinc-200',
  };

  const getExpiry = () => {
    if (userPlan === 'trial') return user?.trial_ends_at ? new Date(user.trial_ends_at).toLocaleDateString('id-ID') : '30 hari dari aktivasi';
    return user?.billing_cycle_end ? new Date(user.billing_cycle_end).toLocaleDateString('id-ID') : 'Aktif';
  };

  const getPlanButtonInfo = (targetPlan) => {
    const targetRank = planOrder[targetPlan] || 0;
    if (targetPlan === userPlan) return { label: 'Plan Aktif', disabled: true, variant: 'outline', className: '' };
    if (targetRank > userRank) return { label: `Upgrade ke ${planConfig[targetPlan]?.name}`, disabled: false, variant: 'default', className: 'bg-indigo-600 hover:bg-indigo-700 text-white' };
    return { label: `Downgrade ke ${planConfig[targetPlan]?.name}`, disabled: false, variant: 'outline', className: 'border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/10' };
  };

  const planCards = [
    { id: 'starter', icon: <Rocket className="w-3 h-3 inline" />, colorAccent: 'blue', priceColor: 'text-slate-900 dark:text-zinc-50', features: ['1 AI Agent', '1.700 pesan/bln', 'Semua channel'] },
    { id: 'growth', icon: null, colorAccent: 'purple', priceColor: 'text-purple-600 dark:text-purple-400', features: ['3 AI Agent', '4.600 pesan/bln', 'Priority Support'], recommended: true },
    { id: 'business', icon: <Crown className="w-3 h-3 inline" />, colorAccent: 'amber', priceColor: 'text-amber-600 dark:text-amber-400', features: ['10 AI Agent', '10.000 pesan/bln', 'API Access'] },
    { id: 'enterprise', icon: <Building2 className="w-3 h-3 inline" />, colorAccent: 'slate', priceColor: 'text-slate-700 dark:text-zinc-300', features: ['Unlimited Agent', 'Unlimited pesan', 'SLA 99.9%'] },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const statusBadge = (status) => {
    const map = {
      'Paid': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      'Pending': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      'Failed': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return map[status] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="space-y-6">
      {/* Pending Downgrade Banner */}
      {pendingDowngrade?.has_pending && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4 flex items-center justify-between" data-testid="pending-downgrade-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                Downgrade ke {pendingDowngrade.new_plan?.toUpperCase()} dijadwalkan
              </p>
              <p className="text-xs text-orange-600 dark:text-orange-400">
                Berlaku pada {formatDate(pendingDowngrade.effective_at)}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="border-orange-300 text-orange-600 hover:bg-orange-100 dark:text-orange-400 dark:bg-orange-950/40" onClick={handleCancelDowngrade}>
            Batalkan
          </Button>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-6 flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-500" />
          Paket Saat Ini
        </h2>
        <div className={`bg-gradient-to-r ${colorClasses[current.color]} rounded-xl p-4 sm:p-6 border`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h3 className="text-xl font-bold">{current.name}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/50 dark:bg-black/20 whitespace-nowrap">{current.badge}</span>
              </div>
              <p className="text-sm opacity-80">
                {userPlan === 'trial' ? '30 hari akses penuh' : billingPeriod === 'yearly' ? 'per tahun' : 'per bulan'}
              </p>
            </div>
            <div className="sm:text-right shrink-0">
              <p className="text-2xl font-bold whitespace-nowrap">{current.price}</p>
              {current.price !== 'Custom' && current.price !== 'Rp 0' && (
                <p className="text-sm opacity-70">/{billingPeriod === 'yearly' ? 'tahun' : 'bulan'}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {current.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 opacity-90">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-current/20">
            <div className="text-sm opacity-80">
              {userPlan === 'trial' ? (
                <span className="flex items-center gap-2">Sisa waktu trial: <TrialCountdownSmall expiresAt={user?.trial_expires_at} /></span>
              ) : (
                <>Berlaku hingga: <span className="font-medium">{getExpiry()}</span></>
              )}
            </div>
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Aktif</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pay As You Go */}
      <PayAsYouGoCard token={token} />

      {/* Plan Cards */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-2 flex items-center gap-2">
          <Zap className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          Ubah Paket
        </h2>
        <p className="text-sm text-slate-500 dark:text-zinc-400 mb-6">
          Upgrade atau downgrade sesuai kebutuhan bisnis Anda
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
          {planCards.map(card => {
            const btn = getPlanButtonInfo(card.id);
            const pc = planConfig[card.id];
            const isEnterprise = card.id === 'enterprise';
            const isCurrentPlan = card.id === userPlan;
            const targetRank = planOrder[card.id] || 0;
            const isDowngradeAction = targetRank < userRank;

            return (
              <div key={card.id} className={`border-2 rounded-xl p-4 transition-colors relative flex flex-col ${isCurrentPlan ? `border-${card.colorAccent}-500 bg-${card.colorAccent}-50/50 dark:bg-${card.colorAccent}-900/10` : 'border-slate-200 dark:border-zinc-800 hover:border-slate-400'}`} data-testid={`plan-card-${card.id}`}>
                {card.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-purple-600 text-white text-[10px] px-2 py-1 rounded-full font-medium whitespace-nowrap">RECOMMENDED</span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm text-slate-900 dark:text-zinc-50">{pc.name}</h3>
                  {card.icon && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-zinc-800">{card.icon}</span>}
                </div>
                <div className="mb-3">
                  <p className={`text-lg font-bold ${card.priceColor}`}>{pc.price}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">{isEnterprise ? 'Hubungi kami' : '/bulan'}</p>
                </div>
                <ul className="space-y-2 mb-4 text-xs text-slate-700 dark:text-zinc-300 flex-1">
                  {card.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                {isEnterprise ? (
                  <Button className="w-full mt-auto" variant="outline" size="sm" onClick={() => window.location.href = 'mailto:sales@nerava.id'} data-testid="enterprise-contact-btn">
                    Hubungi Sales
                  </Button>
                ) : (
                  <Button
                    className={`w-full mt-auto ${btn.className}`}
                    variant={btn.variant}
                    size="sm"
                    disabled={btn.disabled}
                    onClick={() => handlePlanAction(card.id)}
                    data-testid={`plan-action-${card.id}`}
                  >
                    {isDowngradeAction && <ArrowDownRight className="w-3.5 h-3.5 mr-1" />}
                    {!isDowngradeAction && targetRank > userRank && <ArrowUpRight className="w-3.5 h-3.5 mr-1" />}
                    {btn.label}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-6 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          Riwayat Pembayaran
        </h2>
        {historyLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-500 dark:text-zinc-400" /></div>
        ) : paymentHistory.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-slate-500 dark:text-zinc-500 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-zinc-400">Belum ada pembayaran berhasil</p>
            <p className="text-sm text-slate-500 dark:text-zinc-500">Pembayaran paket & kredit pesan yang lunas akan muncul di sini</p>
          </div>
        ) : (
          <>
            {/* Mobile: kartu per pembayaran */}
            <div className="sm:hidden divide-y divide-slate-100 dark:divide-zinc-800" data-testid="payment-history-list-mobile">
              {paymentHistory.map((p, i) => (
                <div key={i} className="py-3 first:pt-0" data-testid={`payment-history-item-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${p.kind === 'topup'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'}`}>
                          {p.kind === 'topup' ? 'Kredit Pesan' : 'Paket'}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusBadge(p.status)}`}>{p.status}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 capitalize truncate">
                        {p.kind === 'topup'
                          ? `${new Intl.NumberFormat('id-ID').format(p.messages || 0)} pesan`
                          : `${p.plan || '-'}${p.billing_period === 'yearly' ? ' · tahunan' : ''}`}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-zinc-500 mt-0.5">{formatDate(p.created_at)}</p>
                    </div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50 tabular-nums whitespace-nowrap">
                      Rp {new Intl.NumberFormat('id-ID').format(p.amount || 0)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: tabel */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="payment-history-table">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-zinc-800 text-left">
                    <th className="pb-3 pr-3 font-medium text-slate-500 whitespace-nowrap dark:text-zinc-400">Tanggal</th>
                    <th className="pb-3 pr-3 font-medium text-slate-500 dark:text-zinc-400">Jenis</th>
                    <th className="pb-3 pr-3 font-medium text-slate-500 dark:text-zinc-400">Keterangan</th>
                    <th className="pb-3 pr-3 font-medium text-slate-500 dark:text-zinc-400">Nominal</th>
                    <th className="pb-3 pr-3 font-medium text-slate-500 dark:text-zinc-400">Status</th>
                    <th className="pb-3 font-medium text-slate-500 dark:text-zinc-400">Referensi</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((p, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-zinc-800/50" data-testid={`payment-history-row-${i}`}>
                      <td className="py-3 pr-3 text-slate-700 dark:text-zinc-300 whitespace-nowrap">{formatDate(p.created_at)}</td>
                      <td className="py-3 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${p.kind === 'topup'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'}`}>
                          {p.kind === 'topup' ? 'Kredit Pesan' : 'Paket'}
                        </span>
                      </td>
                      <td className="py-3 pr-3 font-medium text-slate-900 dark:text-zinc-50 capitalize whitespace-nowrap">
                        {p.kind === 'topup'
                          ? `${new Intl.NumberFormat('id-ID').format(p.messages || 0)} pesan`
                          : `${p.plan || '-'}${p.billing_period === 'yearly' ? ' · tahunan' : ''}`}
                      </td>
                      <td className="py-3 pr-3 text-slate-700 dark:text-zinc-300 tabular-nums whitespace-nowrap">Rp {new Intl.NumberFormat('id-ID').format(p.amount || 0)}</td>
                      <td className="py-3 pr-3"><span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(p.status)}`}>{p.status}</span></td>
                      <td className="py-3 text-xs text-slate-500 font-mono truncate max-w-[160px] dark:text-zinc-400">{p.reference || p.merchant_order_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* FAQ */}
      <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-800 p-6">
        <h3 className="font-medium text-blue-900 dark:text-indigo-200 mb-4">
          <HelpCircle className="w-4 h-4 inline mr-1" />Pertanyaan Umum
        </h3>
        <div className="space-y-3 text-sm text-indigo-700 dark:text-indigo-300">
          <div>
            <p className="font-medium">Apa perbedaan antar paket?</p>
            <p className="text-indigo-600 dark:text-indigo-400">Perbedaan utama adalah jumlah AI Agent dan kuota pesan per bulan. Fitur lainnya sama di semua paket berbayar.</p>
          </div>
          <div>
            <p className="font-medium">Apakah bisa downgrade paket?</p>
            <p className="text-indigo-600 dark:text-indigo-400">Ya, Anda bisa downgrade kapan saja. Downgrade dijadwalkan di akhir billing cycle. AI Agent yang melebihi limit akan dinonaktifkan.</p>
          </div>
          <div>
            <p className="font-medium">Bagaimana cara pembayaran?</p>
            <p className="text-indigo-600 dark:text-indigo-400">Pembayaran bulanan via transfer bank, e-wallet (OVO, GoPay, Dana), atau QRIS.</p>
          </div>
        </div>
      </div>

      {/* Downgrade Modal */}
      {downgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="downgrade-modal">
          <div className="bg-white dark:bg-zinc-900 rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
                <ArrowDownRight className="w-5 h-5 text-orange-500" />
                Downgrade ke {planConfig[downgradeModal.plan]?.name}
              </h3>
              <button onClick={() => setDowngradeModal(null)} className="text-slate-500 hover:text-slate-600 dark:text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {downgradeModal.requiresSelection ? (
              <>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 mb-4">
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    Plan {planConfig[downgradeModal.plan]?.name} memperbolehkan maksimal <strong>{downgradeModal.maxKeep} AI Agent</strong>.
                    Anda memiliki {downgradeModal.agents.length} agent. Pilih {downgradeModal.maxKeep} agent yang ingin dipertahankan.
                  </p>
                </div>
                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                  {downgradeModal.agents.map(agent => (
                    <label key={agent.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedKeepAgents.includes(agent.id) ? 'border-blue-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-slate-200 dark:border-zinc-800 hover:border-slate-400'}`}>
                      <input
                        type="checkbox"
                        checked={selectedKeepAgents.includes(agent.id)}
                        onChange={() => toggleKeepAgent(agent.id)}
                        className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-blue-500 dark:text-indigo-400"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-zinc-50">{agent.name}</p>
                        <p className="text-xs text-slate-500 dark:text-zinc-400">{agent.total_chats || 0} percakapan</p>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mb-4 dark:text-zinc-400">
                  Dipilih: {selectedKeepAgents.length}/{downgradeModal.maxKeep}
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-600 dark:text-zinc-400 mb-4">
                Downgrade akan dijadwalkan di akhir periode billing Anda. Anda tetap menikmati plan saat ini sampai saat itu.
              </p>
            )}

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDowngradeModal(null)}>
                Batal
              </Button>
              <Button
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleConfirmDowngrade}
                disabled={downgradeProcessing || (downgradeModal.requiresSelection && selectedKeepAgents.length === 0)}
                data-testid="confirm-downgrade-btn"
              >
                {downgradeProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Konfirmasi Downgrade
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



const SettingsPage = () => {
  const { user, token, logout, checkAuth } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const t = (key) => getTranslation(language, key);
  
  // Determine active tab from URL path
  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes('/settings/shipping') || path.includes('/settings/pengiriman')) return 'pengiriman';
    if (path.includes('/settings/payment') || path.includes('/settings/pembayaran')) return 'pembayaran';
    if (path.includes('/settings/notifications') || path.includes('/settings/notifikasi')) return 'notifikasi';
    if (path.includes('/settings/follow-up')) return 'followup';
    if (path.includes('/settings/invoice')) return 'invoice';
    if (path.includes('/settings/subscription') || path.includes('/settings/langganan')) return 'langganan';
    return 'profil';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());
  
  // Profile state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Business Profile state (from onboarding)
  const [businessProfile, setBusinessProfile] = useState({
    store_name: '',
    business_category: '',
    business_description: '',
    business_type: 'physical',
    // Legacy fields (untuk backward compatibility)
    address: '',
    city: '',
    postal_code: '',
    // New detailed address fields
    street_address: '',
    destination_id: '',
    subdistrict_name: '',
    district_name: '',
    province_name: '',
    full_address: ''
  });
  const [savingBusiness, setSavingBusiness] = useState(false);

  // Shipping state - with option to use business address
  const [useBusinessAddress, setUseBusinessAddress] = useState(true);
  const [cities, setCities] = useState([]);
  const [shippingSettings, setShippingSettings] = useState({
    city_id: '',
    city_name: '',
    street_address: '',
    destination_id: '',
    subdistrict_name: '',
    district_name: '',
    province_name: '',
    postal_code: '',
    full_address: '',
    default_weight: 500
  });
  const [loadingCities, setLoadingCities] = useState(true);
  const [savingShipping, setSavingShipping] = useState(false);
  // Jam operasional toko (WIB) — menentukan estimasi hari kirim produk fisik
  const [operatingHours, setOperatingHours] = useState({
    working_days: [0, 1, 2, 3, 4, 5],
    open_time: '09:00',
    close_time: '17:00',
    cutoff_time: null
  });
  const [nextShipping, setNextShipping] = useState(null);
  const [savingHours, setSavingHours] = useState(false);

  // Payment state
  const [availableMethods, setAvailableMethods] = useState({ banks: [], ewallets: [] });
  const [userPaymentMethods, setUserPaymentMethods] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [savingPayment, setSavingPayment] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [newPayment, setNewPayment] = useState({
    method_key: '',
    account_number: '',
    account_name: ''
  });

  // Payment Mode state (Manual/Otomatis)
  const [paymentMode, setPaymentMode] = useState('manual'); // 'manual' or 'automatic'
  const [qrisImageUrl, setQrisImageUrl] = useState('');
  const [qrisName, setQrisName] = useState('');
  const [qrisUploading, setQrisUploading] = useState(false);
  const [qrisEnabled, setQrisEnabled] = useState(false);
  const [disbursementSettings, setDisbursementSettings] = useState({
    bank_code: '',
    account_number: '',
    account_name: '',
    schedule: 'instant', // 'instant', 'daily', 'weekly', 'manual'
    active_methods: {
      va_bca: true,
      va_mandiri: true,
      va_bni: true,
      va_bri: true,
      qris: true,
      ewallet_ovo: false,
      ewallet_gopay: false,
      ewallet_dana: false
    }
  });
  const [loadingPaymentMode, setLoadingPaymentMode] = useState(true);
  const [savingPaymentMode, setSavingPaymentMode] = useState(false);

  // Indonesian banks for disbursement
  const disbursementBanks = [
    { code: '014', name: 'BCA' },
    { code: '008', name: 'Mandiri' },
    { code: '009', name: 'BNI' },
    { code: '002', name: 'BRI' },
    { code: '022', name: 'CIMB Niaga' },
    { code: '013', name: 'Permata' },
    { code: '011', name: 'Danamon' },
    { code: '016', name: 'Maybank' },
    { code: '019', name: 'Panin' },
    { code: '028', name: 'OCBC NISP' },
    { code: '426', name: 'Mega' },
    { code: '046', name: 'DBS' },
    { code: '023', name: 'UOB' },
    { code: '213', name: 'BTPN' },
    { code: '950', name: 'Bank Jago' },
    { code: '501', name: 'BPR KS' },
    { code: '110', name: 'Bank Jabar' },
    { code: '111', name: 'Bank DKI' },
    { code: '112', name: 'Bank DIY' },
    { code: '113', name: 'Bank Jateng' },
    { code: '114', name: 'Bank Jatim' },
    { code: '536', name: 'BCA Digital' },
    { code: '542', name: 'Bank Neo Commerce' },
    { code: '212', name: 'Bank Woori Saudara' },
    { code: 'other', name: 'Bank Lainnya' }
  ];

  // Notification state
  const [notificationSettings, setNotificationSettings] = useState({
    whatsapp: { enabled: false, phone_number: '', verified: false },
    telegram: { enabled: false, chat_id: '', verified: false },
    email: { enabled: false, address: '', verified: true },
    notify_new_order: true,
    notify_low_stock: true,
    notify_payment: false
  });
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [verifyingTelegram, setVerifyingTelegram] = useState(false);
  const [testingChannel, setTestingChannel] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Invoice state - business_name and business_address now come from user profile (centralized)
  const [invoiceSettings, setInvoiceSettings] = useState({
    business_phone: '',
    business_email: '',
    logo_url: '',
    footer_note: 'Terima kasih atas pesanan Anda!',
    reminder_enabled: false,
    reminder_days: [1, 3, 7]
  });
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTabFromPath());
  }, [location.pathname]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Navigate to the corresponding URL
    const tabToPath = {
      'profil': '/settings/profile',
      'pengiriman': '/settings/shipping',
      'pembayaran': '/settings/payment',
      'notifikasi': '/settings/notifications',
      'followup': '/settings/follow-up',
      // 'invoice': '/settings/invoice', // Hidden: fitur dinonaktifkan sementara
      'langganan': '/settings/subscription'
    };
    navigate(tabToPath[tab] || '/settings/profile');
  };

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      // Load business profile from user data (including new address fields)
      setBusinessProfile({
        store_name: user.store_name || '',
        business_category: user.business_category || '',
        business_description: user.business_description || '',
        business_type: user.business_type || 'physical',
        // Legacy fields
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || '',
        // New detailed address fields
        street_address: user.street_address || user.address || '',
        destination_id: user.destination_id || '',
        subdistrict_name: user.subdistrict_name || '',
        district_name: user.district_name || '',
        city_name: user.city_name || user.city || '',
        province_name: user.province_name || '',
        full_address: user.full_address || ''
      });
      fetchCities();
      fetchShippingSettings();
      fetchOperatingHours();
      fetchAvailablePaymentMethods();
      fetchUserPaymentMethods();
      fetchPaymentMode();
      fetchNotificationSettings();
      fetchInvoiceSettings();
    }
  }, [user]);

  // Shipping functions
  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/shipping/cities`);
      if (response.data.success) {
        setCities(response.data.cities || []);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchShippingSettings = async () => {
    try {
      const response = await axios.get(
        `${API}/shipping/settings?user_id=${user.id}`,
        { headers }
      );
      if (response.data.success && response.data.settings) {
        const s = response.data.settings;
        const loc = s.store_location || {};
        setShippingSettings({
          city_id: loc.city_id || '',
          city_name: loc.city_name || '',
          street_address: loc.street_address || '',
          destination_id: loc.destination_id || '',
          subdistrict_name: loc.subdistrict_name || '',
          district_name: loc.district_name || '',
          province_name: loc.province_name || '',
          postal_code: loc.postal_code || '',
          full_address: loc.full_address || '',
          default_weight: s.default_weight || 500
        });
        // Load the address source preference
        setUseBusinessAddress(s.use_business_address !== false); // Default to true if not set
      }
    } catch (error) {
      console.error('Error fetching shipping settings:', error);
    }
  };

  const fetchOperatingHours = async () => {
    try {
      const res = await axios.get(`${API}/shipping/operating-hours`, { headers });
      if (res.data.success) {
        setOperatingHours(res.data.hours);
        setNextShipping(res.data.next_shipping);
      }
    } catch (error) {
      console.error('Error fetching operating hours:', error);
    }
  };

  const handleSaveOperatingHours = async () => {
    if (!(operatingHours.working_days || []).length) {
      toast.error('Pilih minimal satu hari kerja');
      return;
    }
    setSavingHours(true);
    try {
      const res = await axios.post(`${API}/shipping/operating-hours`, operatingHours, { headers });
      setOperatingHours(res.data.hours);
      setNextShipping(res.data.next_shipping);
      toast.success('Jam operasional disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan jam operasional');
    } finally {
      setSavingHours(false);
    }
  };

  // Handle address selection for BUSINESS PROFILE
  const handleBusinessAddressSelect = (locationData) => {
    if (locationData) {
      setBusinessProfile(prev => ({
        ...prev,
        destination_id: locationData.destination_id,
        subdistrict_name: locationData.subdistrict_name,
        district_name: locationData.district_name,
        city_name: locationData.city_name,
        city: locationData.city_name, // Legacy field
        province_name: locationData.province_name,
        postal_code: locationData.postal_code
      }));
    } else {
      setBusinessProfile(prev => ({
        ...prev,
        destination_id: '',
        subdistrict_name: '',
        district_name: '',
        city_name: '',
        city: '',
        province_name: '',
        postal_code: ''
      }));
    }
  };

  // Handle address selection for SHIPPING SETTINGS
  const handleAddressSelect = (locationData) => {
    if (locationData) {
      // Find city_id from the city name
      const selectedCity = cities.find(c => 
        c.name.toLowerCase().includes(locationData.city_name.toLowerCase()) ||
        locationData.city_name.toLowerCase().includes(c.name.toLowerCase())
      );
      
      setShippingSettings(prev => ({
        ...prev,
        destination_id: locationData.destination_id,
        subdistrict_name: locationData.subdistrict_name,
        district_name: locationData.district_name,
        city_name: locationData.city_name,
        province_name: locationData.province_name,
        postal_code: locationData.postal_code,
        city_id: selectedCity?.city_id || prev.city_id
      }));
    } else {
      // Clear location data
      setShippingSettings(prev => ({
        ...prev,
        destination_id: '',
        subdistrict_name: '',
        district_name: '',
        city_name: '',
        province_name: '',
        postal_code: ''
      }));
    }
  };

  // Copy business address to shipping settings
  const copyBusinessAddressToShipping = () => {
    if (businessProfile.destination_id) {
      const selectedCity = cities.find(c => 
        c.name.toLowerCase().includes(businessProfile.city_name?.toLowerCase() || '') ||
        (businessProfile.city_name?.toLowerCase() || '').includes(c.name.toLowerCase())
      );
      
      setShippingSettings(prev => ({
        ...prev,
        street_address: businessProfile.street_address,
        destination_id: businessProfile.destination_id,
        subdistrict_name: businessProfile.subdistrict_name,
        district_name: businessProfile.district_name,
        city_name: businessProfile.city_name || businessProfile.city,
        province_name: businessProfile.province_name,
        postal_code: businessProfile.postal_code,
        city_id: selectedCity?.city_id || prev.city_id
      }));
      toast.success('Alamat dari profil bisnis berhasil disalin');
    } else {
      toast.error('Alamat profil bisnis belum lengkap. Silakan lengkapi terlebih dahulu.');
    }
  };

  const handleSaveShipping = async (e) => {
    e.preventDefault();
    
    // If using business address, validate business profile has address
    if (useBusinessAddress) {
      if (!businessProfile.destination_id) {
        toast.error('Alamat profil bisnis belum lengkap. Silakan lengkapi di tab Profil terlebih dahulu.');
        return;
      }
      // Copy business address to shipping
      const selectedCity = cities.find(c => 
        c.name.toLowerCase().includes(businessProfile.city_name?.toLowerCase() || '') ||
        (businessProfile.city_name?.toLowerCase() || '').includes(c.name.toLowerCase())
      );
      
      const fullAddress = `${businessProfile.street_address}, ${businessProfile.subdistrict_name}, ${businessProfile.district_name}, ${businessProfile.city_name || businessProfile.city}, ${businessProfile.province_name} ${businessProfile.postal_code}`;
      
      setSavingShipping(true);
      try {
        await axios.post(
          `${API}/shipping/settings?user_id=${user.id}`,
          {
            street_address: businessProfile.street_address,
            destination_id: businessProfile.destination_id,
            subdistrict_name: businessProfile.subdistrict_name,
            district_name: businessProfile.district_name,
            city_name: businessProfile.city_name || businessProfile.city,
            province_name: businessProfile.province_name,
            postal_code: businessProfile.postal_code,
            city_id: selectedCity?.city_id,
            full_address: fullAddress,
            default_weight: shippingSettings.default_weight,
            use_business_address: true
          },
          { headers }
        );
        toast.success('Pengaturan pengiriman disimpan (menggunakan alamat profil bisnis)');
      } catch (error) {
        toast.error('Gagal menyimpan pengaturan');
      } finally {
        setSavingShipping(false);
      }
      return;
    }
    
    // Using custom shipping address
    if (!shippingSettings.destination_id) {
      toast.error('Pilih lokasi pengiriman terlebih dahulu');
      return;
    }
    if (!shippingSettings.street_address.trim()) {
      toast.error('Masukkan alamat jalan');
      return;
    }
    setSavingShipping(true);
    try {
      // Construct full address
      const fullAddress = `${shippingSettings.street_address}, ${shippingSettings.subdistrict_name}, ${shippingSettings.district_name}, ${shippingSettings.city_name}, ${shippingSettings.province_name} ${shippingSettings.postal_code}`;
      
      await axios.post(
        `${API}/shipping/settings?user_id=${user.id}`,
        {
          ...shippingSettings,
          full_address: fullAddress,
          use_business_address: false  // Using custom address
        },
        { headers }
      );
      toast.success('Pengaturan pengiriman disimpan');
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSavingShipping(false);
    }
  };

  const handleCityChange = (cityId) => {
    const city = cities.find(c => c.city_id === parseInt(cityId));
    setShippingSettings(prev => ({
      ...prev,
      city_id: parseInt(cityId),
      city_name: city?.name || ''
    }));
  };

  // Payment Mode functions (Manual/Automatic)
  const fetchPaymentMode = async () => {
    try {
      setLoadingPaymentMode(true);
      const response = await axios.get(
        `${API}/payment/mode?user_id=${user.id}`,
        { headers }
      );
      if (response.data.success) {
        setPaymentMode(response.data.mode || 'manual');
        if (response.data.disbursement_settings) {
          setDisbursementSettings(prev => ({
            ...prev,
            ...response.data.disbursement_settings
          }));
        }
      }
      // Fetch QRIS settings
      const qrisRes = await axios.get(`${API}/payment/qris?user_id=${user.id}`, { headers });
      if (qrisRes.data.success) {
        setQrisImageUrl(qrisRes.data.qris_image_url || '');
        setQrisName(qrisRes.data.qris_name || '');
        setQrisEnabled(!!qrisRes.data.qris_image_url);
      }
    } catch (error) {
      console.error('Error fetching payment mode:', error);
    } finally {
      setLoadingPaymentMode(false);
    }
  };

  const handleSavePaymentMode = async () => {
    if (paymentMode === 'automatic') {
      if (!disbursementSettings.bank_code || !disbursementSettings.account_number || !disbursementSettings.account_name) {
        toast.error('Lengkapi data rekening pencairan untuk mode otomatis');
        return;
      }
    }
    
    setSavingPaymentMode(true);
    try {
      await axios.post(
        `${API}/payment/mode?user_id=${user.id}`,
        {
          mode: paymentMode,
          disbursement_settings: paymentMode === 'automatic' ? disbursementSettings : null
        },
        { headers }
      );
      toast.success('Pengaturan pembayaran disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan');
    } finally {
      setSavingPaymentMode(false);
    }
  };

  // QRIS upload handler
  const handleQrisUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar (JPG, PNG, dll)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }
    setQrisUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result;
        const res = await axios.post(
          `${API}/payment/qris?user_id=${user.id}`,
          { qris_image: base64, qris_name: qrisName || 'QRIS' },
          { headers }
        );
        if (res.data.success) {
          setQrisImageUrl(res.data.qris_image_url);
          toast.success('QRIS berhasil diupload');
        }
      } catch (error) {
        toast.error('Gagal mengupload QRIS');
      } finally {
        setQrisUploading(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleQrisDelete = async () => {
    if (!confirm('Hapus gambar QRIS?')) return;
    try {
      await axios.delete(`${API}/payment/qris?user_id=${user.id}`, { headers });
      setQrisImageUrl('');
      setQrisName('');
      toast.success('QRIS berhasil dihapus');
    } catch (error) {
      toast.error('Gagal menghapus QRIS');
    }
  };

  // Payment functions
  const fetchAvailablePaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment/methods/available`);
      if (response.data.success) {
        setAvailableMethods({
          banks: response.data.banks || [],
          ewallets: response.data.ewallets || []
        });
      }
    } catch (error) {
      console.error('Error fetching available payment methods:', error);
    }
  };

  const fetchUserPaymentMethods = async () => {
    try {
      const response = await axios.get(
        `${API}/payment/methods?user_id=${user.id}`,
        { headers }
      );
      if (response.data.success) {
        setUserPaymentMethods(response.data.methods || []);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!newPayment.method_key || !newPayment.account_number || !newPayment.account_name) {
      toast.error('Lengkapi semua field');
      return;
    }
    setSavingPayment(true);
    try {
      await axios.post(
        `${API}/payment/methods?user_id=${user.id}`,
        newPayment,
        { headers }
      );
      toast.success('Metode pembayaran ditambahkan');
      setNewPayment({ method_key: '', account_number: '', account_name: '' });
      setShowAddPayment(false);
      fetchUserPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan metode pembayaran');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleTogglePaymentActive = async (method) => {
    try {
      await axios.put(
        `${API}/payment/methods/${method.id}?user_id=${user.id}`,
        { is_active: !method.is_active },
        { headers }
      );
      fetchUserPaymentMethods();
    } catch (error) {
      toast.error('Gagal mengupdate status');
    }
  };

  const handleDeletePayment = async (methodId) => {
    if (!confirm('Hapus metode pembayaran ini?')) return;
    try {
      await axios.delete(
        `${API}/payment/methods/${methodId}?user_id=${user.id}`,
        { headers }
      );
      toast.success('Metode pembayaran dihapus');
      fetchUserPaymentMethods();
    } catch (error) {
      toast.error('Gagal menghapus metode pembayaran');
    }
  };

  // Notification functions
  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(
        `${API}/notification-settings?user_id=${user.id}`,
        { headers }
      );
      if (response.data.success && response.data.settings) {
        const settings = response.data.settings;
        setNotificationSettings(settings);
        
        // Auto-create settings in DB for new users (so they don't need to click Save first)
        if (!settings.updated_at) {
          // This is default settings, save it to DB so test notifications work
          await axios.put(
            `${API}/notification-settings?user_id=${user.id}`,
            settings,
            { headers }
          );
        }
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(
        `${API}/notification-settings?user_id=${user.id}`,
        notificationSettings,
        { headers }
      );
      toast.success('Pengaturan notifikasi berhasil disimpan');
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan notifikasi');
    } finally {
      setSavingNotifications(false);
    }
  };

  // Auto-save when notification settings change
  const autoSaveNotifications = async (newSettings) => {
    try {
      await axios.put(
        `${API}/notification-settings?user_id=${user.id}`,
        newSettings,
        { headers }
      );
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const handleVerifyTelegram = async () => {
    const chatId = notificationSettings.telegram.chat_id;
    if (!chatId) {
      toast.error(t('settings.enterTelegramId') || 'Masukkan Chat ID Telegram');
      return;
    }
    setVerifyingTelegram(true);
    try {
      const response = await axios.post(
        `${API}/notification-settings/telegram/verify?user_id=${user.id}&chat_id=${chatId}`,
        {},
        { headers }
      );
      if (response.data.success) {
        toast.success(t('settings.telegramVerified') || 'Telegram berhasil diverifikasi! Cek pesan di Telegram Anda.');
        setNotificationSettings(prev => ({
          ...prev,
          telegram: { ...prev.telegram, verified: true }
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings.telegramVerifyFailed') || 'Gagal verifikasi Telegram');
    } finally {
      setVerifyingTelegram(false);
    }
  };

  const handleTestNotification = async (channel) => {
    setTestingChannel(channel);
    try {
      const response = await axios.post(
        `${API}/notification-settings/test?user_id=${user.id}&channel=${channel}`,
        {},
        { headers }
      );
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim test notifikasi');
    } finally {
      setTestingChannel('');
    }
  };

  // Invoice functions
  const fetchInvoiceSettings = async () => {
    setLoadingInvoice(true);
    try {
      const response = await axios.get(`${API}/invoices/settings`, { headers });
      if (response.data.settings) {
        setInvoiceSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleSaveInvoiceSettings = async () => {
    setSavingInvoice(true);
    try {
      await axios.post(`${API}/invoices/settings`, invoiceSettings, { headers });
      toast.success('Pengaturan invoice berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan invoice');
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result;
        const response = await axios.post(`${API}/invoices/settings/upload-logo`, { logo: base64 }, { headers });
        // Use URL from ImgBB if available, otherwise use base64
        const logoUrl = response.data?.url || base64;
        setInvoiceSettings({ ...invoiceSettings, logo_url: logoUrl });
        toast.success('Logo berhasil diupload');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Gagal upload logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const toggleReminderDay = (day) => {
    const days = invoiceSettings.reminder_days || [];
    if (days.includes(day)) {
      setInvoiceSettings({ ...invoiceSettings, reminder_days: days.filter(d => d !== day) });
    } else {
      setInvoiceSettings({ ...invoiceSettings, reminder_days: [...days, day].sort((a, b) => a - b) });
    }
  };

  const updateNotificationChannel = (channel, field, value) => {
    const newSettings = {
      ...notificationSettings,
      [channel]: { ...notificationSettings[channel], [field]: value }
    };
    setNotificationSettings(newSettings);
    // Auto-save after toggle changes
    autoSaveNotifications(newSettings);
  };

  const updateNotificationType = (type, value) => {
    const newSettings = { ...notificationSettings, [type]: value };
    setNotificationSettings(newSettings);
    // Auto-save after checkbox changes
    autoSaveNotifications(newSettings);
  };

  // Profile functions
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t('profile.nameEmpty'));
      return;
    }
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, { name }, { headers, withCredentials: true });
      await checkAuth();
      toast.success(t('profile.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile.updateFailed'));
    } finally {
      setSavingProfile(false);
    }
  };

  // Business Profile update
  const handleUpdateBusinessProfile = async (e) => {
    e.preventDefault();
    if (!businessProfile.store_name.trim()) {
      toast.error('Nama toko wajib diisi');
      return;
    }
    setSavingBusiness(true);
    try {
      await axios.put(`${API}/auth/business-profile`, businessProfile, { headers });
      toast.success('Profil bisnis berhasil disimpan');
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan profil bisnis');
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t('profile.passwordFieldsRequired'));
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t('profile.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers, withCredentials: true });
      toast.success(t('profile.passwordSuccess'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile.passwordUpdateFailed'));
    } finally {
      setSavingPassword(false);
    }
  };

  const allPaymentOptions = [
    ...availableMethods.banks.map(b => ({ ...b, category: 'Bank' })),
    ...availableMethods.ewallets.map(e => ({ ...e, category: 'E-Wallet' }))
  ];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] transition-colors">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <div className="lg:ml-64">
          {/* Fixed Header */}
          <header className="fixed top-0 right-0 left-0 lg:left-64 bg-white dark:bg-zinc-900 border-b border-slate-200 dark:border-zinc-800 z-20">
            <div className="px-4 py-3 sm:py-4 flex items-center justify-between">
              <div className="flex items-center gap-3 sm:gap-4">
                <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600 dark:text-zinc-300 p-1">
                  <Menu className="w-6 h-6" />
                </button>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 truncate" style={{ fontFamily: 'Manrope' }}>
                    {activeTab === 'profil' && 'Profil'}
                    {activeTab === 'pengiriman' && 'Pengaturan Pengiriman'}
                    {activeTab === 'pembayaran' && 'Pembayaran Toko'}
                    {activeTab === 'notifikasi' && 'Pengaturan Notifikasi'}
                    {activeTab === 'followup' && 'Follow Up Otomatis'}
                    {/* activeTab === 'invoice' && 'Pengaturan Invoice' */}
                    {activeTab === 'langganan' && 'Langganan Nerava'}
                  </h1>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-400 truncate">
                    {activeTab === 'profil' && 'Kelola informasi akun Anda'}
                    {activeTab === 'pengiriman' && 'Atur lokasi toko untuk perhitungan ongkir'}
                    {activeTab === 'pembayaran' && 'Kelola rekening untuk terima pembayaran customer'}
                    {activeTab === 'notifikasi' && 'Atur notifikasi order dan stok'}
                    {activeTab === 'followup' && 'Kirim pesan follow-up otomatis'}
                    {/* activeTab === 'invoice' && 'Atur template invoice & reminder pembayaran' */}
                    {activeTab === 'langganan' && 'Kelola paket langganan Nerava Anda'}
                  </p>
                </div>
              </div>
            </div>
          </header>

          {/* Main Content with padding for fixed header */}
          <main className="pt-24 sm:pt-28 px-4 pb-4 lg:px-8 lg:pb-8">
            <div className="max-w-5xl mx-auto">
            
            {/* Profil Tab */}
            {activeTab === 'profil' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-6 flex items-center gap-2">
                    <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    {t('profile.info')}
                  </h2>
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="dark:text-zinc-200">{t('profile.name')}</Label>
                      <div className="relative mt-2">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                        <Input
                          id="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="pl-10 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                          placeholder={t('profile.namePlaceholder')}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email" className="dark:text-zinc-200">{t('profile.email')}</Label>
                      <div className="relative mt-2">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                        <Input
                          id="email"
                          value={email}
                          className="pl-10 bg-slate-50 dark:bg-[#0A0A0A] dark:text-zinc-400 dark:border-zinc-700"
                          disabled
                          readOnly
                        />
                      </div>
                      <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">{t('profile.emailNote')}</p>
                    </div>
                    <Button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      disabled={savingProfile}
                    >
                      {savingProfile ? t('profile.saving') : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {t('profile.saveProfile')}
                        </>
                      )}
                    </Button>
                  </form>
                </div>

                {/* Business Profile Section */}
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-6 flex items-center gap-2">
                    <Store className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    {t('profile.businessProfile')}
                  </h2>
                  <form onSubmit={handleUpdateBusinessProfile} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="store_name" className="dark:text-zinc-200">{t('profile.storeName')} *</Label>
                        <Input
                          id="store_name"
                          value={businessProfile.store_name}
                          onChange={(e) => setBusinessProfile({...businessProfile, store_name: e.target.value})}
                          className="mt-2 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                          placeholder={t('profile.storeNamePlaceholder')}
                        />
                      </div>
                      <div>
                        <Label htmlFor="business_category" className="dark:text-zinc-200">{t('profile.businessCategory')}</Label>
                        <select
                          id="business_category"
                          value={businessProfile.business_category}
                          onChange={(e) => setBusinessProfile({...businessProfile, business_category: e.target.value})}
                          className="mt-2 w-full px-3 py-2 border rounded-lg bg-white dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                        >
                          <option value="">{t('profile.selectCategory')}</option>
                          <option value="fashion">{t('onboarding.categories.fashion')}</option>
                          <option value="electronics">{t('onboarding.categories.electronics')}</option>
                          <option value="food">{t('onboarding.categories.food')}</option>
                          <option value="health">{t('onboarding.categories.health')}</option>
                          <option value="home">{t('onboarding.categories.home')}</option>
                          <option value="sports">{t('onboarding.categories.sports')}</option>
                          <option value="automotive">{t('onboarding.categories.automotive')}</option>
                          <option value="books">{t('onboarding.categories.books')}</option>
                          <option value="toys">{t('onboarding.categories.toys')}</option>
                          <option value="services">{t('onboarding.categories.services')}</option>
                          <option value="other">{t('onboarding.categories.other')}</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="dark:text-zinc-200">Jenis produk yang dijual</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2" data-testid="settings-business-type">
                        {[
                          { value: 'physical', label: 'Produk Fisik', note: 'Butuh alamat & ongkir' },
                          { value: 'digital', label: 'Produk Digital', note: 'Dikirim ke email' },
                          { value: 'both', label: 'Keduanya', note: 'Fisik + digital' },
                        ].map((opt) => {
                          const active = (businessProfile.business_type || 'physical') === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setBusinessProfile({ ...businessProfile, business_type: opt.value })}
                              data-testid={`settings-business-type-${opt.value}`}
                              className={`text-left rounded-xl border p-3 transition-colors ${active
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 dark:border-indigo-500'
                                : 'border-slate-200 hover:border-slate-300 dark:border-zinc-800 dark:hover:border-zinc-700'}`}
                            >
                              <p className="text-sm font-medium text-slate-800 dark:text-zinc-200">{opt.label}</p>
                              <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">{opt.note}</p>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-zinc-400 mt-2">
                        Mode Digital menyembunyikan pengaturan ongkir, alamat pengiriman, dan resi. AI hanya meminta nama & email customer.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="business_description" className="dark:text-zinc-200">{t('profile.businessDescription')}</Label>
                      <textarea
                        id="business_description"
                        value={businessProfile.business_description}
                        onChange={(e) => setBusinessProfile({...businessProfile, business_description: e.target.value})}
                        className="mt-2 w-full px-3 py-2 border rounded-lg bg-white dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700 min-h-[80px]"
                        placeholder={t('profile.descriptionPlaceholder')}
                      />
                    </div>
                    
                    {/* Smart Address Section for Business Profile */}
                    <div className="border-t border-slate-200 dark:border-zinc-800 pt-6 mt-6">
                      <h3 className="text-base font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        Alamat Bisnis
                      </h3>
                      
                      {/* Street Address */}
                      <div className="mb-4">
                        <Label className="dark:text-zinc-200 flex items-center gap-2">
                          <Home className="w-4 h-4" />
                          Alamat Jalan
                        </Label>
                        <Input
                          value={businessProfile.street_address}
                          onChange={(e) => setBusinessProfile({...businessProfile, street_address: e.target.value, address: e.target.value})}
                          placeholder="Contoh: Jl. Sudirman No. 123, Gedung ABC Lt. 5"
                          className="mt-2 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                        />
                      </div>
                      
                      {/* Smart Search */}
                      <AddressSearch
                        value={{
                          destination_id: businessProfile.destination_id,
                          subdistrict_name: businessProfile.subdistrict_name,
                          district_name: businessProfile.district_name,
                          city_name: businessProfile.city_name || businessProfile.city,
                          province_name: businessProfile.province_name,
                          postal_code: businessProfile.postal_code
                        }}
                        onChange={handleBusinessAddressSelect}
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      disabled={savingBusiness}
                    >
                      {savingBusiness ? t('profile.savingBusiness') : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {t('profile.saveBusinessProfile')}
                        </>
                      )}
                    </Button>
                  </form>
                </div>

                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-6 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    {t('profile.changePassword')}
                  </h2>
                  <form onSubmit={handleUpdatePassword} className="space-y-4">
                    <div>
                      <Label htmlFor="currentPassword" className="dark:text-zinc-200">{t('profile.currentPassword')}</Label>
                      <div className="relative mt-2">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                        <Input
                          id="currentPassword"
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="pl-10 pr-10 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                          placeholder={t('profile.currentPasswordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 dark:text-zinc-400"
                        >
                          {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="newPassword" className="dark:text-zinc-200">{t('profile.newPassword')}</Label>
                      <div className="relative mt-2">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                        <Input
                          id="newPassword"
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pl-10 pr-10 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                          placeholder={t('profile.newPasswordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 dark:text-zinc-400"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="confirmPassword" className="dark:text-zinc-200">{t('profile.confirmPassword')}</Label>
                      <div className="relative mt-2">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                          placeholder={t('profile.confirmPasswordPlaceholder')}
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      disabled={savingPassword}
                    >
                      {savingPassword ? t('profile.saving') : t('profile.updatePassword')}
                    </Button>
                  </form>
                </div>
              </div>
            )}

            {/* Pengiriman Tab */}
            {activeTab === 'pengiriman' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-2 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Alamat Pengiriman
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-zinc-400 mb-6">
                    Alamat ini digunakan sebagai titik asal untuk perhitungan ongkir
                  </p>

                  <form onSubmit={handleSaveShipping} className="space-y-6">
                    {/* Address Source Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Option 1: Use Business Address */}
                      <div
                        onClick={() => setUseBusinessAddress(true)}
                        className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${
                          useBusinessAddress
                            ? 'border-blue-500 bg-indigo-50 dark:bg-indigo-950/30'
                            : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:hover:border-zinc-700'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            useBusinessAddress ? 'border-blue-500 bg-indigo-500' : 'border-slate-300 dark:border-zinc-700'
                          }`}>
                            {useBusinessAddress && <div className="w-2 h-2 bg-white rounded-full dark:bg-zinc-900" />}
                          </div>
                          <div>
                            <p className={`font-medium ${useBusinessAddress ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-zinc-300'}`}>
                              Gunakan Alamat Profil Bisnis
                            </p>
                            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                              Alamat sama dengan yang diatur di Profil Bisnis
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Option 2: Use Different Address */}
                      <div
                        onClick={() => setUseBusinessAddress(false)}
                        className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${
                          !useBusinessAddress
                            ? 'border-blue-500 bg-indigo-50 dark:bg-indigo-950/30'
                            : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:hover:border-zinc-700'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            !useBusinessAddress ? 'border-blue-500 bg-indigo-500' : 'border-slate-300 dark:border-zinc-700'
                          }`}>
                            {!useBusinessAddress && <div className="w-2 h-2 bg-white rounded-full dark:bg-zinc-900" />}
                          </div>
                          <div>
                            <p className={`font-medium ${!useBusinessAddress ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-zinc-300'}`}>
                              Gunakan Alamat Lain
                            </p>
                            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                              Alamat pengiriman berbeda dari profil (contoh: gudang)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Show Business Address Preview if using business address */}
                    {useBusinessAddress && (
                      <div className="bg-slate-50 dark:bg-zinc-800/50 rounded-xl p-4 border border-slate-200 dark:border-zinc-700">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-700 dark:text-zinc-300 flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-blue-500" />
                              Alamat dari Profil Bisnis
                            </p>
                            {businessProfile.destination_id ? (
                              <div className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
                                <p>{businessProfile.street_address}</p>
                                <p>{businessProfile.subdistrict_name}, {businessProfile.district_name}</p>
                                <p>{businessProfile.city_name || businessProfile.city}, {businessProfile.province_name} {businessProfile.postal_code}</p>
                              </div>
                            ) : (
                              <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                                Alamat profil bisnis belum lengkap. Silakan lengkapi di tab Profil.
                              </p>
                            )}
                          </div>
                          <Link 
                            to="/settings/profile" 
                            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                          >
                            Edit <Edit2 className="w-3 h-3" />
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Show custom address form if NOT using business address */}
                    {!useBusinessAddress && (
                      <>
                        {/* Street Address */}
                        <div>
                          <Label className="dark:text-zinc-200 flex items-center gap-2">
                            <Home className="w-4 h-4" />
                            Alamat Jalan *
                          </Label>
                          <Input
                            value={shippingSettings.street_address}
                            onChange={(e) => setShippingSettings(prev => ({ ...prev, street_address: e.target.value }))}
                            placeholder="Contoh: Jl. Gudang No. 45, Kawasan Industri"
                            className="mt-2 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                          />
                        </div>

                        {/* Smart Address Search */}
                        <AddressSearch
                          value={{
                            destination_id: shippingSettings.destination_id,
                            subdistrict_name: shippingSettings.subdistrict_name,
                            district_name: shippingSettings.district_name,
                            city_name: shippingSettings.city_name,
                            province_name: shippingSettings.province_name,
                            postal_code: shippingSettings.postal_code
                          }}
                          onChange={handleAddressSelect}
                        />
                      </>
                    )}

                    {/* Default Weight */}
                    <div className="border-t border-slate-200 dark:border-zinc-800 pt-6">
                      <Label className="dark:text-zinc-200 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Berat Default per Produk (gram)
                      </Label>
                      <div className="relative mt-2">
                        <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                        <Input
                          type="number"
                          value={shippingSettings.default_weight}
                          onChange={(e) => setShippingSettings(prev => ({ ...prev, default_weight: parseInt(e.target.value) || 500 }))}
                          className="pl-10 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                          placeholder="500"
                          min="100"
                          step="100"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">Digunakan untuk estimasi ongkir jika berat produk tidak diset</p>
                    </div>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
                        disabled={savingShipping}
                      >
                        {savingShipping ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Menyimpan...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Simpan Pengaturan Pengiriman
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>

                {/* Jam Operasional & Cut-off Pengiriman */}
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4 sm:p-6" data-testid="operating-hours-card">
                  <h3 className="font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Jam Operasional & Batas Kirim
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                    Semua jam memakai <strong>WIB (Asia/Jakarta)</strong>. Pesanan yang dibayar setelah jam tutup
                    atau di luar hari kerja akan dikirim di hari kerja berikutnya.
                  </p>

                  <div className="mt-4">
                    <Label className="dark:text-zinc-200">Hari kerja</Label>
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="working-days-picker">
                      {DAY_LABELS.map((day, idx) => {
                        const active = (operatingHours.working_days || []).includes(idx);
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setOperatingHours(prev => {
                              const days = new Set(prev.working_days || []);
                              if (days.has(idx)) { days.delete(idx); } else { days.add(idx); }
                              return { ...prev, working_days: Array.from(days).sort((a, b) => a - b) };
                            })}
                            data-testid={`working-day-${idx}`}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${active
                              ? 'border-indigo-500 bg-indigo-600 text-white'
                              : 'border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 hover:border-slate-300'}`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div>
                      <Label className="dark:text-zinc-200">Jam buka</Label>
                      <Input type="time" value={operatingHours.open_time || '09:00'}
                        onChange={(e) => setOperatingHours(prev => ({ ...prev, open_time: e.target.value }))}
                        className="mt-1 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700" data-testid="open-time-input" />
                    </div>
                    <div>
                      <Label className="dark:text-zinc-200">Jam tutup</Label>
                      <Input type="time" value={operatingHours.close_time || '17:00'}
                        onChange={(e) => setOperatingHours(prev => ({ ...prev, close_time: e.target.value }))}
                        className="mt-1 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700" data-testid="close-time-input" />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!operatingHours.cutoff_time}
                        onChange={(e) => setOperatingHours(prev => ({
                          ...prev, cutoff_time: e.target.checked ? (prev.close_time || '17:00') : null
                        }))}
                        className="mt-1" data-testid="cutoff-toggle" />
                      <span className="text-sm text-slate-700 dark:text-zinc-300">
                        Batas jam kirim berbeda dari jam tutup
                        <span className="block text-xs text-slate-500 dark:text-zinc-400">
                          Contoh: toko tutup 21:00 tapi kurir jemput terakhir 15:00
                        </span>
                      </span>
                    </label>
                    {operatingHours.cutoff_time && (
                      <Input type="time" value={operatingHours.cutoff_time}
                        onChange={(e) => setOperatingHours(prev => ({ ...prev, cutoff_time: e.target.value }))}
                        className="mt-2 w-40 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700" data-testid="cutoff-time-input" />
                    )}
                  </div>

                  {nextShipping && (
                    <div className="mt-4 rounded-lg bg-slate-50 dark:bg-zinc-800/60 p-3 text-sm text-slate-700 dark:text-zinc-300" data-testid="next-shipping-preview">
                      Pesanan yang dibayar sekarang dikirim <strong>{nextShipping.label}</strong>
                      <span className="text-xs text-slate-500 dark:text-zinc-400"> (cut-off {nextShipping.cutoff_time} WIB)</span>
                    </div>
                  )}

                  <Button onClick={handleSaveOperatingHours} disabled={savingHours}
                    className="bg-indigo-600 hover:bg-indigo-700 mt-4 w-full sm:w-auto" data-testid="save-operating-hours-btn">
                    {savingHours ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Menyimpan...</>
                      : <><Save className="w-4 h-4 mr-2" />Simpan Jam Operasional</>}
                  </Button>
                </div>

                <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-800 p-4">
                  <h3 className="font-medium text-blue-900 dark:text-indigo-200 mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Bagaimana cara kerjanya?
                  </h3>
                  <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1 list-disc list-inside">
                    <li>Alamat pengiriman digunakan sebagai titik asal untuk kalkulasi ongkir</li>
                    <li>Saat pelanggan memberikan alamat, AI otomatis menghitung ongkir</li>
                    <li>Ongkir dihitung menggunakan API Komerce dengan kurir JNE, J&T, dan SiCepat</li>
                    <li>Anda bisa menggunakan alamat profil bisnis atau alamat gudang yang berbeda</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Pembayaran Tab */}
            {activeTab === 'pembayaran' && (
              <div className="space-y-6">
                {/* Mode Pembayaran Toggle */}
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <div className="mb-6">
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      Mode Pembayaran
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
                      Pilih cara menerima pembayaran dari pelanggan
                    </p>
                  </div>

                  {/* Toggle Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* Manual Card */}
                    <div
                      onClick={() => setPaymentMode('manual')}
                      className={`cursor-pointer p-5 rounded-xl border-2 transition-all ${
                        paymentMode === 'manual'
                          ? 'border-blue-500 bg-indigo-50 dark:bg-indigo-950/30'
                          : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:hover:border-zinc-700'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                          paymentMode === 'manual' ? 'border-blue-500' : 'border-slate-300'
                        }`}>
                          {paymentMode === 'manual' && <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 dark:text-zinc-50">Manual</h3>
                          <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
                            Pelanggan transfer ke rekening Anda, lalu konfirmasi pembayaran secara manual
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300">Bank Transfer</span>
                            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300">E-Wallet</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Otomatis Card - Visible but disabled (under development) */}
                    <div
                      className="relative p-5 rounded-xl border-2 border-slate-200 dark:border-zinc-800 opacity-60 cursor-not-allowed"
                      data-testid="payment-mode-automatic-card"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full border-2 border-slate-300 dark:border-zinc-700 flex items-center justify-center mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900 dark:text-zinc-50">Otomatis</h3>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 font-medium">Segera Hadir</span>
                          </div>
                          <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
                            Status pembayaran otomatis terupdate & uang langsung masuk ke rekening Anda
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300">Virtual Account</span>
                            <span className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">QRIS</span>
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">E-Wallet</span>
                          </div>
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                            Fitur ini sedang dalam tahap pengembangan
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Settings for Automatic Mode - HIDDEN: masih dalam pengembangan */}

                {/* Manual Mode - Existing Payment Methods */}
                {paymentMode === 'manual' && (
                  <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900 dark:text-zinc-50">
                          Rekening & E-Wallet Anda
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
                          Pelanggan akan transfer manual ke rekening ini
                        </p>
                      </div>
                      <Button
                        onClick={() => setShowAddPayment(true)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Tambah
                      </Button>
                    </div>

                    {/* Add Payment Form */}
                    {showAddPayment && (
                      <div className="mb-6 p-4 bg-slate-50 dark:bg-zinc-800/50 rounded-xl border border-slate-200 dark:border-zinc-700">
                        <h3 className="font-medium text-slate-900 dark:text-zinc-50 mb-4">Tambah Metode Pembayaran</h3>
                        <form onSubmit={handleAddPayment} className="space-y-4">
                          <div>
                            <Label className="dark:text-zinc-200">Pilih Metode *</Label>
                            <Select
                              value={newPayment.method_key}
                              onValueChange={(v) => setNewPayment(prev => ({ ...prev, method_key: v }))}
                            >
                              <SelectTrigger className="mt-2 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700">
                                <SelectValue placeholder="Pilih bank atau e-wallet" />
                              </SelectTrigger>
                              <SelectContent className="dark:bg-zinc-900 max-h-60">
                                <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-zinc-400">Bank</div>
                                {availableMethods.banks.map((bank) => (
                                  <SelectItem key={bank.key} value={bank.key}>
                                    <div className="flex items-center gap-2">
                                      <Building2 className="w-4 h-4" />
                                      {bank.name}
                                    </div>
                                  </SelectItem>
                                ))}
                                <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-zinc-400 mt-2">E-Wallet</div>
                                {availableMethods.ewallets.map((ew) => (
                                  <SelectItem key={ew.key} value={ew.key}>
                                    <div className="flex items-center gap-2">
                                      <Wallet className="w-4 h-4" />
                                      {ew.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {newPayment.method_key && (
                            <>
                              <div>
                                <Label className="dark:text-zinc-200">
                                  {availableMethods.banks.some(b => b.key === newPayment.method_key) ? 'Nomor Rekening *' : 'Nomor HP *'}
                                </Label>
                                <Input
                                  value={newPayment.account_number}
                                  onChange={(e) => setNewPayment(prev => ({ ...prev, account_number: e.target.value }))}
                                  className="mt-2 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                                  placeholder={availableMethods.banks.some(b => b.key === newPayment.method_key) ? "1234567890" : "081234567890"}
                                />
                              </div>
                              <div>
                                <Label className="dark:text-zinc-200">Nama Pemilik *</Label>
                                <Input
                                  value={newPayment.account_name}
                                  onChange={(e) => setNewPayment(prev => ({ ...prev, account_name: e.target.value }))}
                                  className="mt-2 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                                  placeholder="Ahmad Susanto"
                                />
                              </div>
                            </>
                          )}
                          <div className="flex gap-2">
                            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={savingPayment}>
                              {savingPayment ? 'Menyimpan...' : 'Simpan'}
                            </Button>
                            <Button type="button" variant="outline" onClick={() => { setShowAddPayment(false); setNewPayment({ method_key: '', account_number: '', account_name: '' }); }}>
                              Batal
                            </Button>
                          </div>
                        </form>
                      </div>
                    )}

                    {/* Payment Methods List */}
                    {loadingPayments ? (
                      <div className="text-center py-8 text-slate-500 dark:text-zinc-400">
                        <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                        Memuat...
                      </div>
                    ) : userPaymentMethods.length === 0 ? (
                      <div className="text-center py-8">
                        <CreditCard className="w-12 h-12 text-slate-500 dark:text-zinc-500 mx-auto mb-3" />
                        <p className="text-slate-500 dark:text-zinc-400">Belum ada metode pembayaran</p>
                        <p className="text-sm text-slate-500 dark:text-zinc-500">Tambahkan rekening bank atau e-wallet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {userPaymentMethods.map((method) => (
                          <div
                            key={method.id}
                            className={`flex items-center justify-between p-4 rounded-xl border ${
                              method.is_active
                                ? 'bg-white dark:bg-zinc-800 border-slate-200 dark:border-zinc-700'
                                : 'bg-slate-50 dark:bg-zinc-900 border-slate-100 dark:border-zinc-800 opacity-60'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${
                                method.type === 'bank'
                                  ? 'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400'
                                  : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                              }`}>
                                {method.type === 'bank' ? <Building2 className="w-5 h-5" /> : <Wallet className="w-5 h-5" />}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-zinc-50">{method.name}</p>
                                <p className="text-sm text-slate-500 dark:text-zinc-400">
                                  {method.account_number} • a.n {method.account_name}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={method.is_active}
                                onCheckedChange={() => handleTogglePaymentActive(method)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400 dark:bg-red-950/40"
                                onClick={() => handleDeletePayment(method.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Save Button for Manual Mode */}
                    <div className="mt-6">
                      <Button
                        onClick={handleSavePaymentMode}
                        disabled={savingPaymentMode}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {savingPaymentMode ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Menyimpan...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Simpan Mode Pembayaran
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* QRIS Section - Only in Manual Mode */}
                {paymentMode === 'manual' && (
                  <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden" data-testid="qris-settings-card">
                    <div className="flex items-center justify-between p-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600">
                          <QrCode className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-slate-900 dark:text-zinc-50">QRIS</h3>
                          <p className="text-sm text-slate-500 dark:text-zinc-400">Terima pembayaran via QRIS dari semua e-wallet & bank</p>
                        </div>
                      </div>
                      <Switch
                        checked={qrisEnabled}
                        onCheckedChange={(v) => setQrisEnabled(v)}
                        data-testid="qris-toggle"
                      />
                    </div>

                    {qrisEnabled && (
                      <div className="px-6 pb-6 space-y-5 border-t border-slate-100 dark:border-zinc-800 pt-5">
                        {/* Upload / Preview Area */}
                        {qrisImageUrl ? (
                          <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-4 items-start">
                              <div className="w-40 h-40 rounded-xl border-2 border-slate-200 dark:border-zinc-700 overflow-hidden bg-white flex-shrink-0 relative dark:bg-zinc-900">
                                {qrisUploading && (
                                  <div className="absolute inset-0 bg-white/80 dark:bg-[#0A0A0A]/80 flex flex-col items-center justify-center z-10">
                                    <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
                                    <p className="text-xs text-slate-500 mt-1.5 dark:text-zinc-400">Mengupload...</p>
                                  </div>
                                )}
                                <img
                                  src={qrisImageUrl}
                                  alt="QRIS Code"
                                  className="w-full h-full object-contain"
                                  data-testid="qris-preview-image"
                                />
                              </div>
                              <div className="flex-1 space-y-3">
                                <div>
                                  <Label className="text-sm text-slate-700 dark:text-zinc-300">Nama QRIS</Label>
                                  <Input
                                    value={qrisName}
                                    onChange={(e) => setQrisName(e.target.value)}
                                    placeholder="Nama toko / pemilik QRIS"
                                    className="mt-1.5 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                                    data-testid="qris-name-input"
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-indigo-600 hover:bg-indigo-700"
                                    onClick={async () => {
                                      try {
                                        await axios.post(
                                          `${API}/payment/qris?user_id=${user.id}`,
                                          { qris_image: qrisImageUrl, qris_name: qrisName },
                                          { headers }
                                        );
                                        toast.success('Nama QRIS diperbarui');
                                      } catch (err) {
                                        toast.error('Gagal menyimpan');
                                      }
                                    }}
                                    data-testid="qris-save-name-btn"
                                  >
                                    <Save className="w-3.5 h-3.5 mr-1.5" />
                                    Simpan
                                  </Button>
                                  <label className={`${qrisUploading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}>
                                    <Button size="sm" variant="outline" asChild disabled={qrisUploading}>
                                      <span>
                                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                                        Ganti
                                      </span>
                                    </Button>
                                    <input type="file" accept="image/*" className="hidden" onChange={handleQrisUpload} data-testid="qris-replace-input" disabled={qrisUploading} />
                                  </label>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400 dark:bg-red-950/40"
                                    onClick={handleQrisDelete}
                                    disabled={qrisUploading}
                                    data-testid="qris-delete-btn"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                                <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800">
                                  <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                  <p className="text-xs text-green-700 dark:text-green-300">
                                    QRIS aktif. AI akan otomatis mengirimkan kode QRIS ini ke pelanggan saat mereka memilih pembayaran QRIS.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-slate-300 dark:border-zinc-700 rounded-xl p-8 text-center" data-testid="qris-upload-area">
                            {qrisUploading ? (
                              <div className="flex flex-col items-center gap-3">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                <p className="text-sm text-slate-500 dark:text-zinc-400">Mengupload QRIS...</p>
                              </div>
                            ) : (
                              <label className="cursor-pointer flex flex-col items-center gap-3">
                                <div className="p-4 rounded-full bg-slate-100 dark:bg-zinc-800">
                                  <QrCode className="w-8 h-8 text-slate-500 dark:text-zinc-500" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-700 dark:text-zinc-300">Upload Kode QRIS</p>
                                  <p className="text-xs text-slate-500 dark:text-zinc-500 mt-1">JPG, PNG (maks 5MB)</p>
                                </div>
                                <Button size="sm" variant="outline" asChild>
                                  <span>
                                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                                    Pilih Gambar
                                  </span>
                                </Button>
                                <input type="file" accept="image/*" className="hidden" onChange={handleQrisUpload} data-testid="qris-upload-input" />
                              </label>
                            )}
                          </div>
                        )}

                        {/* Tutorial: Cara Dapat QRIS */}
                        <div className="rounded-xl border border-blue-100 dark:border-indigo-800 bg-indigo-50/50 dark:bg-blue-900/10 p-4" data-testid="qris-tutorial">
                          <div className="flex items-center gap-2 mb-3">
                            <HelpCircle className="w-4 h-4 text-blue-500" />
                            <h4 className="text-sm font-semibold text-blue-900 dark:text-indigo-200">Cara Dapat QRIS (Gratis)</h4>
                          </div>
                          <p className="text-xs text-indigo-700 dark:text-indigo-300 mb-3">
                            Anda cukup daftar sebagai merchant QRIS di salah satu penyedia:
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                            <div className="flex items-center gap-2 text-xs text-indigo-700 dark:text-indigo-300 bg-white dark:bg-zinc-900 rounded-lg px-3 py-2 border border-blue-100 dark:border-indigo-800">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                              GoPay Merchant (via GoBiz)
                            </div>
                            <div className="flex items-center gap-2 text-xs text-indigo-700 dark:text-indigo-300 bg-white dark:bg-zinc-900 rounded-lg px-3 py-2 border border-blue-100 dark:border-indigo-800">
                              <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                              OVO Merchant (via Grab Merchant)
                            </div>
                            <div className="flex items-center gap-2 text-xs text-indigo-700 dark:text-indigo-300 bg-white dark:bg-zinc-900 rounded-lg px-3 py-2 border border-blue-100 dark:border-indigo-800">
                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                              DANA Merchant
                            </div>
                            <div className="flex items-center gap-2 text-xs text-indigo-700 dark:text-indigo-300 bg-white dark:bg-zinc-900 rounded-lg px-3 py-2 border border-blue-100 dark:border-indigo-800">
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                              Bank Anda (BCA, BRI, Mandiri, dll)
                            </div>
                          </div>
                          <p className="text-xs text-indigo-600 dark:text-indigo-400">
                            Setelah daftar, Anda dapat 1 gambar QR code statis yang bisa dipakai terus untuk menerima pembayaran dari semua e-wallet dan bank.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Info Box */}
                <div className={`rounded-xl border p-4 ${
                  paymentMode === 'automatic'
                    ? 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800'
                    : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                }`}>
                  <h3 className={`font-medium mb-2 ${
                    paymentMode === 'automatic' ? 'text-blue-900 dark:text-indigo-200' : 'text-amber-900 dark:text-amber-200'
                  }`}>
                    {paymentMode === 'automatic' ? <><Zap className="w-4 h-4 inline mr-1" />Keuntungan Mode Otomatis</> : <><Lightbulb className="w-4 h-4 inline mr-1" />Tips Mode Manual</>}
                  </h3>
                  <ul className={`text-sm space-y-1 list-disc list-inside ${
                    paymentMode === 'automatic' ? 'text-indigo-700 dark:text-indigo-300' : 'text-amber-700 dark:text-amber-300'
                  }`}>
                    {paymentMode === 'automatic' ? (
                      <>
                        <li>Status pembayaran otomatis terupdate (tidak perlu cek manual)</li>
                        <li>Uang langsung masuk ke rekening Anda (instant/harian)</li>
                        <li>AI bisa auto-confirm pesanan yang sudah dibayar</li>
                        <li>Pelanggan bisa bayar via VA, QRIS, atau E-Wallet</li>
                        <li>Mengurangi risiko salah transfer atau penipuan</li>
                      </>
                    ) : (
                      <>
                        <li>AI akan menawarkan metode pembayaran yang aktif ke pelanggan</li>
                        <li>Pastikan nomor rekening dan nama pemilik benar</li>
                        <li>Anda perlu cek mutasi rekening secara manual</li>
                        <li>Konfirmasi pembayaran pelanggan dari halaman Orders</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Notifikasi Tab */}
            {activeTab === 'notifikasi' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2 mb-6">
                    <Bell className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Channel Notifikasi
                  </h2>

                  {loadingNotifications ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-600 dark:text-indigo-400" />
                      <span className="ml-2 text-slate-500 dark:text-zinc-400">Memuat...</span>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Push Notification (PWA) */}
                      <PushNotificationCard />

                      {/* Telegram */}
                      <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-950/40">
                              <Send className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-zinc-50">Telegram</p>
                              <p className="text-xs text-slate-500 dark:text-zinc-400">Notifikasi via Telegram Bot</p>
                            </div>
                          </div>
                          <Switch
                            checked={notificationSettings.telegram.enabled}
                            onCheckedChange={(checked) => updateNotificationChannel('telegram', 'enabled', checked)}
                          />
                        </div>
                        {notificationSettings.telegram.enabled && (
                          <div className="mt-3 space-y-3">
                            <div>
                              <Label className="text-sm text-slate-600 dark:text-zinc-400">Chat ID</Label>
                              <div className="flex gap-2 mt-1">
                                <Input
                                  value={notificationSettings.telegram.chat_id}
                                  onChange={(e) => updateNotificationChannel('telegram', 'chat_id', e.target.value)}
                                  placeholder="123456789"
                                  className="flex-1"
                                />
                                <Button
                                  variant="outline"
                                  onClick={handleVerifyTelegram}
                                  disabled={verifyingTelegram || !notificationSettings.telegram.chat_id}
                                >
                                  {verifyingTelegram ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : notificationSettings.telegram.verified ? (
                                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  ) : (
                                    'Verifikasi'
                                  )}
                                </Button>
                              </div>
                            </div>
                            {notificationSettings.telegram.verified ? (
                              <div className="flex items-center gap-2 text-green-600 text-sm dark:text-green-400">
                                <CheckCircle className="w-4 h-4" />
                                <span>Terverifikasi</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTestNotification('telegram')}
                                  disabled={testingChannel === 'telegram'}
                                  className="ml-auto"
                                >
                                  {testingChannel === 'telegram' ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                  ) : (
                                    <Send className="w-4 h-4 mr-1" />
                                  )}
                                  Test
                                </Button>
                              </div>
                            ) : (
                              <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-lg p-3 text-sm">
                                <p className="font-medium text-blue-900 dark:text-indigo-200 mb-2">Cara setup Telegram:</p>
                                <ol className="text-indigo-700 dark:text-indigo-300 space-y-2 list-decimal list-inside">
                                  <li>
                                    <strong>Start bot Nerava:</strong> Buka Telegram, cari <code className="bg-indigo-100 dark:bg-blue-800 px-1 rounded">@nerava_bot</code> dan ketik <code className="bg-indigo-100 dark:bg-blue-800 px-1 rounded">/start</code>
                                  </li>
                                  <li>
                                    <strong>Dapatkan Chat ID:</strong> Cari <code className="bg-indigo-100 dark:bg-blue-800 px-1 rounded">@userinfobot</code>, ketik <code className="bg-indigo-100 dark:bg-blue-800 px-1 rounded">/start</code>, salin angka setelah &quot;Id:&quot;
                                  </li>
                                  <li>
                                    <strong>Verifikasi:</strong> Paste Chat ID di atas, klik Verifikasi
                                  </li>
                                </ol>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Email */}
                      <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                              <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-zinc-50">Email</p>
                              <p className="text-xs text-slate-500 dark:text-zinc-400">Notifikasi via Email</p>
                            </div>
                          </div>
                          <Switch
                            checked={notificationSettings.email.enabled}
                            onCheckedChange={(checked) => updateNotificationChannel('email', 'enabled', checked)}
                          />
                        </div>
                        {notificationSettings.email.enabled && (
                          <div className="mt-3 space-y-3">
                            <div>
                              <Label className="text-sm text-slate-600 dark:text-zinc-400">Alamat Email</Label>
                              <Input
                                type="email"
                                value={notificationSettings.email.address}
                                onChange={(e) => updateNotificationChannel('email', 'address', e.target.value)}
                                placeholder="owner@tokoku.com"
                                className="mt-1"
                              />
                            </div>
                            {notificationSettings.email.address && (
                              <div className="flex items-center gap-2 text-green-600 text-sm dark:text-green-400">
                                <CheckCircle className="w-4 h-4" />
                                <span>Siap menerima notifikasi</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTestNotification('email')}
                                  disabled={testingChannel === 'email'}
                                  className="ml-auto"
                                >
                                  {testingChannel === 'email' ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                  ) : (
                                    <Send className="w-4 h-4 mr-1" />
                                  )}
                                  Test
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Notification Types */}
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-4">
                    <Pin className="w-4 h-4 inline mr-1" />Terima Notifikasi Untuk
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-zinc-800/50">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-zinc-50">Order Baru Masuk</p>
                        <p className="text-sm text-slate-500 dark:text-zinc-400">Notifikasi saat ada pesanan baru dari chat</p>
                      </div>
                      <Switch
                        checked={notificationSettings.notify_new_order}
                        onCheckedChange={(checked) => updateNotificationType('notify_new_order', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-zinc-800/50">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-zinc-50">Stok Produk Menipis</p>
                        <p className="text-sm text-slate-500 dark:text-zinc-400">Notifikasi saat stok kurang dari 5 pcs</p>
                      </div>
                      <Switch
                        checked={notificationSettings.notify_low_stock}
                        onCheckedChange={(checked) => updateNotificationType('notify_low_stock', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-zinc-800/50">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-zinc-50"><DollarSign className="w-4 h-4 inline mr-1" />Pembayaran Diterima</p>
                        <p className="text-sm text-slate-500 dark:text-zinc-400">Notifikasi saat customer membayar via Duitku (VA, QRIS, E-Wallet)</p>
                      </div>
                      <Switch
                        checked={notificationSettings.notify_payment}
                        onCheckedChange={(checked) => updateNotificationType('notify_payment', checked)}
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveNotifications}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    disabled={savingNotifications}
                  >
                    {savingNotifications ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Simpan Pengaturan
                      </>
                    )}
                  </Button>
                </div>

                {/* Info Box */}
                <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-800 p-4">
                  <h3 className="font-medium text-blue-900 dark:text-indigo-200 mb-2">
                    <Bell className="w-4 h-4 inline mr-1" />Cara Kerja Notifikasi
                  </h3>
                  <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1 list-disc list-inside">
                    <li>Pilih channel notifikasi yang ingin diaktifkan (bisa lebih dari satu)</li>
                    <li>Untuk Telegram, masukkan Chat ID dan verifikasi terlebih dahulu</li>
                    <li>Centang jenis notifikasi yang ingin diterima</li>
                    <li>Klik Simpan untuk menyimpan pengaturan</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Follow Up Tab */}
            {activeTab === 'followup' && (
              <FollowUpSettings />
            )}

            {/* Invoice Tab - Hidden: fitur dinonaktifkan sementara */}
            {false && activeTab === 'invoice' && (
              <div className="space-y-6">
                {/* Business Info for Invoice */}
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-6 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Informasi Bisnis
                  </h2>
                  <div className="space-y-4">
                    {/* Logo Upload */}
                    <div>
                      <Label className="text-sm font-medium">Logo Bisnis</Label>
                      <div className="mt-2 flex items-center gap-4">
                        {invoiceSettings.logo_url ? (
                          <img 
                            src={invoiceSettings.logo_url} 
                            alt="Logo" 
                            className="w-20 h-20 object-contain rounded-lg border border-slate-200 dark:border-zinc-800"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-slate-100 dark:bg-zinc-800 rounded-lg flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-slate-500 dark:text-zinc-400" />
                          </div>
                        )}
                        <div>
                          <input
                            type="file"
                            id="logo-upload"
                            accept="image/*"
                            className="hidden"
                            onChange={handleLogoUpload}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('logo-upload').click()}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Logo
                          </Button>
                          <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">PNG atau JPG, maks 2MB</p>
                        </div>
                      </div>
                    </div>

                    {/* Business Info from Profile (Read-only reference) */}
                    <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mb-1">Nama Bisnis</p>
                          <p className="font-medium text-slate-900 dark:text-zinc-50">
                            {businessProfile.store_name || <span className="text-amber-600 dark:text-amber-400">Nama toko belum diisi</span>}
                          </p>
                          {businessProfile.address && (
                            <p className="text-sm text-slate-600 dark:text-zinc-400 mt-1">
                              {businessProfile.address}{businessProfile.city && `, ${businessProfile.city}`}
                            </p>
                          )}
                        </div>
                        <a 
                          href="/settings/profile" 
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          Edit di Profil
                        </a>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="business_phone">No. Telepon Invoice</Label>
                        <Input
                          id="business_phone"
                          value={invoiceSettings.business_phone}
                          onChange={(e) => setInvoiceSettings({...invoiceSettings, business_phone: e.target.value})}
                          placeholder="08xxxxxxxxxx"
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">Opsional - tampil di invoice</p>
                      </div>
                      <div>
                        <Label htmlFor="business_email">Email Bisnis Invoice</Label>
                        <Input
                          id="business_email"
                          type="email"
                          value={invoiceSettings.business_email}
                          onChange={(e) => setInvoiceSettings({...invoiceSettings, business_email: e.target.value})}
                          placeholder="toko@email.com"
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">Opsional - tampil di invoice</p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="footer_note">Catatan di Invoice</Label>
                      <Input
                        id="footer_note"
                        value={invoiceSettings.footer_note}
                        onChange={(e) => setInvoiceSettings({...invoiceSettings, footer_note: e.target.value})}
                        placeholder="Terima kasih atas pesanan Anda!"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">Teks ini akan muncul di bagian bawah invoice</p>
                    </div>
                  </div>
                </div>

                {/* Payment Reminder Settings */}
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-6 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    Payment Reminder
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <div>
                        <p className="font-medium text-amber-900 dark:text-amber-200">Aktifkan Reminder Otomatis</p>
                        <p className="text-sm text-amber-700 dark:text-amber-300">Kirim email pengingat pembayaran ke customer</p>
                      </div>
                      <Switch
                        checked={invoiceSettings.reminder_enabled}
                        onCheckedChange={(checked) => setInvoiceSettings({...invoiceSettings, reminder_enabled: checked})}
                      />
                    </div>

                    {invoiceSettings.reminder_enabled && (
                      <div>
                        <Label className="mb-2 block">Kirim Reminder Pada:</Label>
                        <div className="flex flex-wrap gap-2">
                          {[1, 2, 3, 5, 7].map((day) => (
                            <Button
                              key={day}
                              variant={invoiceSettings.reminder_days?.includes(day) ? "default" : "outline"}
                              size="sm"
                              onClick={() => toggleReminderDay(day)}
                              className={invoiceSettings.reminder_days?.includes(day) ? "bg-amber-600 hover:bg-amber-700" : ""}
                            >
                              H+{day}
                            </Button>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 mt-2 dark:text-zinc-400">
                          Reminder akan dikirim ke email customer pada hari yang dipilih setelah invoice dibuat
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview & Save */}
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowInvoicePreview(true)}
                    disabled={!businessProfile.store_name}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Preview Template
                  </Button>
                  <Button
                    onClick={handleSaveInvoiceSettings}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    disabled={savingInvoice}
                  >
                    {savingInvoice ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Simpan Pengaturan
                      </>
                    )}
                  </Button>
                </div>

                {/* Info Box */}
                <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-800 p-4">
                  <h3 className="font-medium text-blue-900 dark:text-indigo-200 mb-2">
                    <FileTextIcon className="w-4 h-4 inline mr-1" />Cara Kerja Invoice Otomatis
                  </h3>
                  <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1 list-disc list-inside">
                    <li>AI akan meminta email customer saat konfirmasi pesanan</li>
                    <li>Invoice dibuat otomatis setelah pesanan dikonfirmasi</li>
                    <li>Invoice dikirim ke email customer secara otomatis</li>
                    <li>Download invoice PDF dari halaman detail pesanan</li>
                    <li>Payment reminder dikirim sesuai jadwal yang diatur</li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Langganan Tab */}
            {activeTab === 'langganan' && (
              <SubscriptionTab user={user} token={token} refreshUser={checkAuth} />
            )}

            {/* Invoice Preview Modal */}
            {showInvoicePreview && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-zinc-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">Preview Invoice</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowInvoicePreview(false)}><X className="w-4 h-4" /></Button>
                    </div>
                    
                    {/* Invoice Preview Content */}
                    <div className="border border-slate-200 dark:border-zinc-800 rounded-lg p-6 bg-white text-slate-900 dark:text-zinc-50 dark:bg-zinc-900">
                      <div className="text-center mb-6">
                        {invoiceSettings.logo_url && (
                          <img src={invoiceSettings.logo_url} alt="Logo" className="w-16 h-16 mx-auto mb-2 object-contain" />
                        )}
                        <h2 className="text-xl font-bold">{businessProfile.store_name || 'Nama Bisnis'}</h2>
                        <p className="text-sm text-slate-600">{businessProfile.address}{businessProfile.city && `, ${businessProfile.city}`}</p>
                        <p className="text-sm text-slate-600">{invoiceSettings.business_phone} | {invoiceSettings.business_email}</p>
                      </div>
                      
                      <div className="text-center mb-6">
                        <h3 className="text-lg font-bold">INVOICE #INV-20260114-001</h3>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                        <div>
                          <p><b>Tanggal:</b> 14 Jan 2026</p>
                          <p><b>Status:</b> BELUM LUNAS</p>
                        </div>
                        <div>
                          <p><b>Customer:</b> Budi Santoso</p>
                          <p><b>Telepon:</b> 081234567890</p>
                        </div>
                      </div>
                      
                      <table className="w-full text-sm mb-4">
                        <thead>
                          <tr className="bg-slate-100 dark:bg-zinc-800">
                            <th className="p-2 text-left">Produk</th>
                            <th className="p-2 text-center">Qty</th>
                            <th className="p-2 text-right">Harga</th>
                            <th className="p-2 text-right">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-2">Produk Contoh</td>
                            <td className="p-2 text-center">2</td>
                            <td className="p-2 text-right">Rp 50.000</td>
                            <td className="p-2 text-right">Rp 100.000</td>
                          </tr>
                        </tbody>
                      </table>
                      
                      <div className="text-right text-sm">
                        <p>Subtotal: <b>Rp 100.000</b></p>
                        <p>Ongkir (JNE): <b>Rp 15.000</b></p>
                        <p className="text-lg mt-2">TOTAL: <b>Rp 115.000</b></p>
                      </div>
                      
                      <div className="mt-6 p-3 bg-slate-50 rounded text-sm dark:bg-zinc-900">
                        <p><b>Pembayaran:</b> Bank Transfer BCA</p>
                        <p>1234567890 a.n. Toko Contoh</p>
                      </div>
                      
                      <p className="text-center mt-6 text-slate-600 text-sm">{invoiceSettings.footer_note}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
